import { v4 as uuid } from 'uuid'
import { mixtilesAxios } from 'utils/ApiUtils'
import { reportGroupOrderCompletedAnalytics } from 'services/Analytics/orderGroupAnalytics'
import { logger } from 'services/logger'
import { normalizeAddress } from 'stores/addressStore/address.utils'
import { getOrderShippingOption } from 'features/productArrivals/cartShippingOptions'
import OrderManager, {
  applyPreOrderSideEffects,
  applyPostOrderSideEffects,
  wasCouponRedeemedInOrders,
} from './OrderManager'
import UserManager from './UserManager'
import { experimentManager } from './ExperimentManager/ExperimentManager'
import type {
  GroupOrderParams,
  GroupOrderResult,
} from './GroupOrderManager.types'

export async function createGroupOrder({
  cartPayloads,
  checkoutId,
  deliveryPromise,
  hasUpToDateDeliveryDates,
  address,
  paymentMethod,
  paymentMethodId,
  paymentIntentId,
  orderPriceSummaries,
  shouldValidateAddress,
  topazLabsEnabled,
  requestConfig,
  bagAnalytics,
}: GroupOrderParams): Promise<GroupOrderResult> {
  const orderRequestParams = cartPayloads.map((payload, index) =>
    OrderManager.getOrderParams({
      deliveryPromise: getOrderShippingOption({
        shippingOption: deliveryPromise,
        productType: payload.productType,
        hasUpToDateDeliveryDates,
      }),
      address,
      paymentMethod,
      paymentMethodId,
      paymentIntentId,
      tiles: payload.orderTiles,
      orderItems: payload.orderItems,
      orderPriceSummary: orderPriceSummaries[index],
      shouldValidateAddress,
      topazLabsEnabled,
    })
  ) as Record<string, unknown>[]

  const orders = orderRequestParams.map((orderParams, index) => ({
    ...orderParams,
    productType: cartPayloads[index].productType,
    localId: uuid(),
    customerEmail: (orderParams.user as { email?: string })?.email,
  }))

  // Every entry in orderPriceSummaries is a clone of the ONE group summary (only `products`
  // differs per entry), so its totalPrice already holds the full group total — don't sum them.
  // A stale `{}` summary at submit time has no totalPrice; submitting it would send a bogus
  // total the server rejects with price_mismatch. Fail fast instead. A total of 0 is still
  // valid (e.g. fully covered by a gift card) — only a missing price is not.
  const groupTotalPrice = orderPriceSummaries[0]?.totalPrice ?? NaN
  if (!Number.isFinite(groupTotalPrice)) {
    logger.error('Group order submitted without a priced summary', null, {
      summaryTotalPrices: orderPriceSummaries.map(
        (summary) => summary.totalPrice
      ),
      orderCount: orderPriceSummaries.length,
      checkoutId,
    })
    throw new Error('Group order price summary is missing a total price')
  }

  const paymentData = OrderManager.preparePaymentData({
    totalPrice: groupTotalPrice,
    paymentMethod,
    paymentMethodId,
    paymentIntentId,
  })

  const normalizedAddress = normalizeAddress(address, !shouldValidateAddress)

  const groupPayload = {
    orders,
    address: normalizedAddress,
    paymentData,
    checkoutId,
    clientTotalCost: groupTotalPrice,
    currency: orderPriceSummaries[0]?.currency,
    isPostPurchaseUpsellEnabled:
      experimentManager.getVariantKey('email_order_confirmation_aug26') ===
      'treatment',
  }

  logger.info('Sending group order request', {
    productTypes: orders.map((o) => o.productType),
    clientTotalCost: groupTotalPrice,
    summaryTotalPrices: orderPriceSummaries.map(
      (summary) => summary.totalPrice
    ),
    checkoutId,
  })

  await applyPreOrderSideEffects({ email: orders[0]?.customerEmail })

  const config = requestConfig || { headers: {} }
  const response = await mixtilesAxios.post('v5/order/group', groupPayload, {
    ...config,
    timeout: 60000,
  })

  const result = response.data as GroupOrderResult
  logger.info('Finished creating group order')
  if (!deliveryPromise || !hasUpToDateDeliveryDates) {
    // Its own Sentry issue, so an alert can count orders placed before checkout had their dates.
    logger.error('Group order placed without a delivery date', null, {
      fingerprint: ['delivery-dates', 'order-without-date'],
      level: 'warning',
      orderGroupUid: result.orderGroup.uid,
      checkoutId,
    })
  }

  const initialUserParams = await UserManager.getInitialUserParams()
  reportGroupOrderCompletedAnalytics({
    orderGroup: result.orderGroup,
    orders: result.orders,
    cartPayloads,
    orderRequestParams,
    orderPriceSummaries,
    address,
    deliveryPromise,
    paymentMethod,
    initialUserParams,
    isFirstOrder: result.isFirstOrder,
    emailAddedRecently: result.emailAddedRecently,
    bagAnalytics,
  })

  const hasUsedCredits =
    orderRequestParams.some((params) => params.coupon || params.redemptions) ||
    orderPriceSummaries.some((summary) => summary.freeTiles)
  applyPostOrderSideEffects({
    deliveryPromise,
    hasUsedCredits,
    wasDiscountCouponRedeemed: wasCouponRedeemedInOrders(result.orders),
    isGiftCard: false,
  })

  return result
}
